/**
 * Fabs that exist, as starting points.
 *
 * Every one of these is a real business with a real reason for the tools it
 * has not got, and that is the whole reason they are here rather than a
 * "small / medium / large" slider. A pure-play foundry has no crystal puller
 * because polished wafers arrive on a truck; an OSAT has no lithography at
 * all; a power-discrete line has no CMP and no plating because aluminium can
 * be plasma etched and copper cannot, so there is nothing to damascene. The
 * assistant offers these, the author edits from there, and `flow/coverage.ts`
 * tells them what each one can and cannot run - measured against the product
 * route, not asserted here.
 *
 * NOTHING IN THIS FILE SAYS WHAT A FLOOR CANNOT DO. That would be a second
 * source of truth about the same fact and it would rot the first time a
 * route changed. The tool list is the claim; everything else is derived.
 */

import { inFlowOrder } from './catalogue'

export interface Archetype {
  id: string
  /** Stations, in whatever order; laid out in flow order when applied. */
  tools: string[]
}

/**
 * GOWN-00, FAC-06 and GAS-19 are on every one of these.
 *
 * The airlock because it is the way in. The facilities face and the bulk gas
 * because there is no such thing as a fab without them: every tool here
 * hangs off process vacuum, chilled water, bulk nitrogen and exhaust, and a
 * floor that leaves them out has hidden the half of the building that
 * actually fails. An author may still delete them - this is a starting
 * point, not a rule - and the assistant will say what that means.
 */
const HOUSE = ['gowning', 'facilities', 'gas']

const FRONT_END = ['clean', 'implant', 'thermal', 'deposition', 'litho', 'etch', 'metrology']

const BACK_END = ['inspection', 'probe', 'dicing', 'bonding', 'packaging', 'finaltest']

export const ARCHETYPES: readonly Archetype[] = [
  {
    // An integrated device manufacturer: sand in one door, binned parts out
    // the other. Everything, which is what Fab A and Fab B already are -
    // including the quantum device station, because an IDM with a research
    // line is exactly who builds qubits on its own tools.
    id: 'idm',
    tools: [
      ...HOUSE,
      'crystal',
      'waferprep',
      'epi',
      ...FRONT_END,
      'euv',
      'ebeam',
      'patterning',
      'cmp',
      'plating',
      ...BACK_END,
      'quantum',
    ],
  },
  {
    // A pure-play foundry: buys polished wafers, ships finished wafers, and
    // never sees a package. The substrate and the back end are somebody
    // else's business, and both are a truck ride away. A leading-edge one
    // runs EUV, and measures every exposure on a CD-SEM.
    id: 'foundry',
    tools: [...HOUSE, 'epi', ...FRONT_END, 'euv', 'ebeam', 'patterning', 'cmp', 'plating', 'inspection', 'probe'],
  },
  {
    // A substrate supplier. Not a wafer fab at all - it is where the wafer a
    // fab starts with comes from, and the reason CZ-07 and WPR-08 are in
    // this simulation in the first place.
    id: 'substrate',
    tools: [...HOUSE, 'crystal', 'waferprep', 'clean', 'epi', 'metrology'],
  },
  {
    // An assembly and test house. No lithography anywhere on the floor: what
    // arrives is a finished wafer and what leaves is a binned part.
    id: 'osat',
    tools: [...HOUSE, ...BACK_END],
  },
  {
    // A power-discrete line. Thick epi, deep implants, long furnace steps,
    // aluminium metal - and so no damascene loop, which is why CMP-11 and
    // ECD-12 are absent and why that absence is a design decision rather
    // than an omission.
    id: 'power',
    tools: [...HOUSE, 'epi', ...FRONT_END, ...BACK_END],
  },
  {
    // The smallest floor that still makes a patterned layer: deposit,
    // expose, etch, measure, with a clean before and a furnace for the
    // oxide. Nine stations, one loop, and every tool on it is one the
    // student will use in the first week.
    id: 'teaching',
    tools: [...HOUSE, 'clean', 'deposition', 'litho', 'etch', 'thermal', 'metrology'],
  },
]

export const archetype = (id: string): Archetype | null =>
  ARCHETYPES.find((a) => a.id === id) ?? null

/** The stations an archetype stands up, in the order a wafer meets them. */
export const archetypeTools = (id: string): string[] => {
  const a = archetype(id)
  return a ? inFlowOrder(a.tools) : []
}
